import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { MessageCircle, X, Send } from "lucide-react";
import { useAskSpotter } from "@/context/AskSpotterContext";

const GREETING = {
  role: "assistant",
  content: "Hi — I'm your Spotter 👋 Ask me to find shoes, check sizes, or help with shipping, returns and your orders.",
  products: [],
};
const CHIPS = [
  "Black sneakers under ₹3000",
  "What's your return policy?",
  "I wear UK 9 — what's my EU size?",
  "Track my order",
];
const PRODUCT_CHIPS = [
  "What size should I buy?",
  "Does it run small?",
  "Is it good for daily wear?",
  "Any cheaper alternatives?",
];
const productGreeting = (p) => ({
  role: "assistant",
  content: `Ask me anything about "${p.title}" — sizing, fit, materials, or whether it's right for you.`,
  products: [],
});

export default function AskSpotter() {
  const { open, setOpen, product, scopeSeq, openGeneral } = useAskSpotter();
  const [msgs, setMsgs] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const bodyRef = useRef(null);

  // Reset the conversation whenever the scope (general ⇄ a specific product) changes.
  useEffect(() => {
    setMsgs([product ? productGreeting(product) : GREETING]);
    setInput("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeSeq]);

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [msgs, busy, open]);

  const chips = product ? PRODUCT_CHIPS : CHIPS;

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || busy) return;
    const history = [...msgs, { role: "user", content, products: [] }];
    setMsgs(history);
    setInput("");
    setBusy(true);
    try {
      const payload = history.filter((m, i) => !(i === 0 && m.role === "assistant"))
        .map((m) => ({ role: m.role, content: m.content }));
      const { data } = await api.post("/chat", { messages: payload, product_id: product?.id || null });
      setMsgs((m) => [...m, { role: "assistant", content: data.reply, products: data.products || [], picks: data.picks || [] }]);
    } catch (e) {
      const detail = e.response?.data?.detail;
      setMsgs((m) => [...m, { role: "assistant", content: typeof detail === "string" ? detail : "Sorry, I couldn't reply just now — please try again.", products: [] }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {/* Launcher */}
      <button
        onClick={() => (open ? setOpen(false) : openGeneral())}
        data-testid="ask-spotter-btn"
        aria-label="Ask a Spotter"
        style={{ bottom: "calc(1.25rem + var(--consent-h, 0px))" }}
        className="fixed right-5 z-[60] flex items-center gap-2 bg-foreground text-background pl-4 pr-5 py-3 font-mono text-[11px] uppercase tracking-widest hover:bg-[hsl(var(--accent))] transition-colors shadow-[4px_4px_0_hsl(var(--foreground))]"
      >
        {open ? <X size={16} strokeWidth={1.5} /> : <MessageCircle size={16} strokeWidth={1.5} />}
        Ask a Spotter
      </button>

      {open && (
        <div style={{ bottom: "calc(5rem + var(--consent-h, 0px))" }}
          className="fixed right-5 z-[60] w-[min(92vw,380px)] h-[min(72vh,560px)] bg-background border-2 border-foreground flex flex-col shadow-[6px_6px_0_hsl(var(--foreground))]">
          <div className="px-4 py-3 border-b border-foreground flex items-center justify-between">
            <div>
              <p className="font-serif text-lg leading-none tracking-tight">Ask a Spotter</p>
              <p className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground mt-1">Certif. № TS-2026</p>
            </div>
            <button onClick={() => setOpen(false)} aria-label="close" className="hover:text-[hsl(var(--accent))]"><X size={18} strokeWidth={1.5} /></button>
          </div>

          <div ref={bodyRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
            {msgs.map((m, i) => (
              <div key={i}>
                <div className={m.role === "user" ? "text-right" : ""}>
                  <span className={`inline-block max-w-[85%] text-left text-[13px] leading-relaxed px-3 py-2 ${m.role === "user" ? "bg-foreground text-background" : "bg-[hsl(var(--muted))] text-foreground"}`}>
                    {m.content}
                  </span>
                </div>
                {m.picks?.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {m.picks.map((pk) => (
                      <div key={pk.product.id} className="border border-foreground/25">
                        <p className="bg-foreground text-background px-2 py-1 font-mono text-[9px] uppercase tracking-widest">{pk.label}</p>
                        <Link to={`/product/${pk.product.id}`} onClick={() => setOpen(false)} className="flex items-center gap-3 p-2 hover:bg-[hsl(var(--muted))] transition-colors">
                          <div className="w-12 h-12 bg-secondary overflow-hidden shrink-0">
                            {pk.product.image && <img src={pk.product.image} alt="" className="w-full h-full object-cover" />}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground truncate">{pk.product.brand}</p>
                            <p className="font-serif text-sm leading-tight truncate">{pk.product.title}</p>
                          </div>
                          <p className="font-mono text-xs shrink-0">₹{Number(pk.product.price).toLocaleString("en-IN")}</p>
                        </Link>
                        {pk.reason && <p className="px-2 pb-2 text-[12px] text-muted-foreground leading-snug">{pk.reason}</p>}
                      </div>
                    ))}
                  </div>
                )}
                {m.products?.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {m.products.map((p) => (
                      <Link key={p.id} to={`/product/${p.id}`} onClick={() => setOpen(false)}
                        className="flex items-center gap-3 border border-foreground/25 p-2 hover:border-foreground transition-colors">
                        <div className="w-12 h-12 bg-secondary overflow-hidden shrink-0">
                          {p.image && <img src={p.image} alt="" className="w-full h-full object-cover" />}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground truncate">{p.brand}</p>
                          <p className="font-serif text-sm leading-tight truncate">{p.title}</p>
                        </div>
                        <p className="font-mono text-xs shrink-0">₹{Number(p.price).toLocaleString("en-IN")}</p>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {busy && <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Spotter is thinking…</p>}

            {msgs.length === 1 && (
              <div className="flex flex-wrap gap-2 pt-1">
                {chips.map((c) => (
                  <button key={c} onClick={() => send(c)}
                    className="border border-foreground/30 px-2.5 py-1.5 font-mono text-[10px] hover:bg-foreground hover:text-background transition-colors">
                    {c}
                  </button>
                ))}
              </div>
            )}
          </div>

          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="border-t border-foreground p-2 flex items-center gap-2">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about shoes, sizes, orders…"
              data-testid="ask-spotter-input"
              className="flex-1 bg-transparent px-2 py-2 text-sm outline-none"
            />
            <button type="submit" disabled={busy || !input.trim()} aria-label="send"
              className="bg-foreground text-background p-2.5 hover:bg-[hsl(var(--accent))] disabled:opacity-40">
              <Send size={15} strokeWidth={1.5} />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
